import { AlertTriangle, CheckCircle2, Info, Lock, ShieldAlert, WifiOff } from 'lucide-react';
import { cn } from '@/lib/cn';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger' | 'locked' | 'offline';

const TONE: Readonly<Record<AlertTone, { readonly Icon: typeof Info; readonly classes: string }>> = {
  info: {
    Icon: Info,
    classes:
      'border-[var(--color-info)]/35 bg-[var(--color-info-soft)] text-[var(--color-info)]',
  },
  success: {
    Icon: CheckCircle2,
    classes:
      'border-[var(--color-success)]/35 bg-[var(--color-success-soft)] text-[var(--color-success)]',
  },
  warning: {
    Icon: AlertTriangle,
    classes:
      'border-[var(--color-warning)]/35 bg-[var(--color-warning-soft)] text-[var(--color-warning)]',
  },
  danger: {
    Icon: ShieldAlert,
    classes:
      'border-[var(--color-danger)]/35 bg-[var(--color-danger-soft)] text-[var(--color-danger)]',
  },
  locked: {
    Icon: Lock,
    classes:
      'border-[var(--color-warning)]/35 bg-[var(--color-warning-soft)] text-[var(--color-warning)]',
  },
  offline: {
    Icon: WifiOff,
    classes:
      'border-[var(--color-danger)]/35 bg-[var(--color-danger-soft)] text-[var(--color-danger)]',
  },
};

export interface AlertProps {
  readonly tone?: AlertTone | undefined;
  readonly title: React.ReactNode;
  readonly children?: React.ReactNode | undefined;
  readonly className?: string | undefined;
  /**
   * assertive interrupts the screen reader immediately. Use it for a failure the user is
   * actively waiting on, such as a rejected sign in. Use polite for background news.
   */
  readonly liveness?: 'polite' | 'assertive' | 'off';
}

export function Alert({
  tone = 'info',
  title,
  children,
  className,
  liveness = 'polite',
}: AlertProps): React.JSX.Element {
  const { Icon, classes } = TONE[tone];
  return (
    <div
      // role=alert already implies assertive; an explicit aria-live lets a caller soften it.
      role={liveness === 'assertive' ? 'alert' : 'status'}
      aria-live={liveness === 'off' ? undefined : liveness}
      className={cn(
        'flex items-start gap-2.5 rounded-[var(--radius-control)] border px-3 py-2.5 text-sm',
        classes,
        className,
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0">
        <p className="font-semibold">{title}</p>
        {children !== undefined ? (
          <div className="mt-0.5 text-xs leading-relaxed opacity-90">{children}</div>
        ) : null}
      </div>
    </div>
  );
}
